import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authErrorMessage, confirmSignUp, resendCode } from '../auth/cognito';
import {
  AuthLayout,
  FormAlert,
  describedBy,
  readAuthState,
  type AuthRouteState,
} from '../components/AuthLayout';

const CODE_LENGTH = 6;

/** Long enough that a second tap cannot outrun the first email, short enough to feel like a fix. */
const RESEND_COOLDOWN_SECONDS = 30;

export function VerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const arrival = readAuthState(location.state);

  // The email is a real field, not just router state: a reload of this URL
  // drops the state, and the code is worthless without the address it belongs
  // to. Prefilled when we have it, typed in when we do not.
  const [email, setEmail] = useState(arrival.email ?? '');
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState(arrival.notice);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);
    setNotice(undefined);
    const address = email.trim();
    try {
      await confirmSignUp(address, code);
      const state: AuthRouteState = {
        email: address,
        notice: 'Your email is confirmed. Sign in to start building your portfolio.',
      };
      navigate('/login', { state, replace: true });
    } catch (caught: unknown) {
      setError(authErrorMessage(caught, 'confirm'));
      setBusy(false);
    }
  }

  async function onResend() {
    if (busy || cooldown > 0) return;

    setBusy(true);
    setError(null);
    setNotice(undefined);
    const address = email.trim();
    try {
      await resendCode(address);
      setNotice(`We sent a new code to ${address}. It replaces the previous one.`);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (caught: unknown) {
      setError(authErrorMessage(caught, 'resend'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Confirm your email"
      subtitle={`Enter the ${CODE_LENGTH}-digit code we emailed you.`}
      footer={
        <>
          Wrong address? <Link className="link" to="/signup">Start over</Link>
        </>
      }
    >
      <form className="stack" onSubmit={onSubmit} aria-busy={busy}>
        <fieldset className="fieldset" disabled={busy}>
          {notice === undefined ? null : <FormAlert kind="success">{notice}</FormAlert>}
          {error === null ? null : (
            <FormAlert kind="error" id="verify-error">
              {error}
            </FormAlert>
          )}

          <div className="field">
            <label className="label" htmlFor="verify-email">
              Email
            </label>
            <input
              id="verify-email"
              className="input"
              type="email"
              name="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={error === null ? undefined : true}
              aria-describedby={describedBy(error !== null && 'verify-error')}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="verify-code">
              Confirmation code
            </label>
            <input
              id="verify-code"
              className="input mono"
              type="text"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              // Non-digits are stripped as they are typed rather than bounced
              // back from Cognito as a code mismatch.
              value={code}
              onChange={(event) =>
                setCode(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))
              }
              minLength={CODE_LENGTH}
              maxLength={CODE_LENGTH}
              required
              autoFocus={email !== ''}
              aria-invalid={error === null ? undefined : true}
              aria-describedby={describedBy('verify-code-hint', error !== null && 'verify-error')}
            />
            <p className="hint" id="verify-code-hint">
              Codes expire after 24 hours. Check your spam folder if it has not arrived.
            </p>
          </div>

          <button type="submit" className="btn btn--primary btn--block">
            {busy ? <span className="spinner spinner--sm" aria-hidden="true" /> : null}
            {busy ? 'Confirming…' : 'Confirm email'}
          </button>

          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={() => void onResend()}
            disabled={cooldown > 0 || email.trim() === ''}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Send a new code'}
          </button>
        </fieldset>
      </form>
    </AuthLayout>
  );
}
